import { Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import {
  lineElementClasses,
  markElementClasses,
  ChartsGrid,
  CurveType,
  LineChartPro,
  ChartsXAxis,
  LinePlot,
  MarkPlot,
} from "@mui/x-charts-pro";
import { CustomItemTooltip } from "../../../../../../ResuableChartTooltip/CustomItemTooltip";
import { useSelector } from "react-redux";
import { useMemo } from "react";

interface EngagementMetric {
  sessions: number;
  bounce_rate: number;
  date: string;
  avg_engagement_time_per_session: string;
  scrolled_users: number;
  click_out_rate: number;
}

const ScrolledUsers = () => {
  const { engagementMetricsDetails } = useSelector(
    (state: any) => state.landingPageExperience
  );

  // Process and sort the data
  const sortedData = useMemo(() => {
    if (!engagementMetricsDetails || !Array.isArray(engagementMetricsDetails)) {
      return [];
    }

    // Sort by date (ascending order)
    const sorted = [...engagementMetricsDetails].sort(
      (a: EngagementMetric, b: EngagementMetric) =>
        new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    return sorted.map((item: EngagementMetric) => {
      const d = new Date(item.date);
      return {
        date: d.toLocaleDateString("en-GB").replace(/\//g, "-"),
        scrolled_users: item.scrolled_users,
      };
    });
  }, [engagementMetricsDetails]);

  // Create chart configuration
  const chartProps = useMemo(
    () => ({
      height: 300,
      series: [
        {
          data: sortedData.map((p) => p.scrolled_users),
          color: "#6200EE",
          curve: "linear" as CurveType,
        },
      ],
    }),
    [sortedData]
  );

  // Handle empty data state
  if (!sortedData.length) {
    return (
      <>
        <Typography fontSize={18}>Scrolled Users</Typography>
        <Typography sx={{ color: "#00000099" }}>
          Count of users who scrolled past a certain threshold on the page
        </Typography>
        <Box
          sx={{
            height: 300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#00000099",
          }}
        >
          No data available
        </Box>
      </>
    );
  }

  return (
    <>
      <Typography fontSize={18} mt={3}>
        Scrolled Users
      </Typography>
      <Typography sx={{ color: "#00000099" }}>
        Count of users who scrolled past a certain threshold on the page
      </Typography>

      <Stack sx={{ width: "100%", mb: "16px" }}>
        <Box sx={{ width: "100%" }}>
          <LineChartPro
            {...chartProps}
            xAxis={[
              {
                scaleType: "band",
                id: "my-x-axis",
                data: sortedData.map((p) => p.date),
                disableLine: true,
                disableTicks: false,
              },
            ]}
            yAxis={[
              {
                id: "y-axis",
                scaleType: "linear",
                position: "right",
                disableLine: true,
                disableTicks: true,
              },
            ]}
            margin={{ left: 25, right: 45, top: 25, bottom: 25 }}
            sx={{
              [`& .${lineElementClasses.root}`]: {
                strokeWidth: 2,
                "&:hover": {
                  strokeWidth: "4",
                },
              },
              [`& .MuiChartsAxis-tickLabel`]: {
                fill: "grey !important", // Label color for X and Y axes
              },
              [`& .MuiChartsAxis-tick`]: {
                stroke: "grey !important", // Tick color for X and Y axes
              },
              [`& .MuiChartsLegend-root text`]: {
                fill: "grey !important", // Change legend text color
              },
              [`& .${markElementClasses.root}`]: {
                scale: "0.6",
                strokeWidth: 1,
                "&:hover": {
                  scale: "1.2",
                  strokeWidth: "2",
                  opacity: "0.6",
                },
              },
            }}
            slotProps={{
              legend: {
                direction: "row",
                position: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                labelStyle: {
                  fontSize: 12,
                },
                itemMarkWidth: 12,
                itemMarkHeight: 2,
                markGap: 8,
                itemGap: 10,
                padding: 10,
              },
            }}
            leftAxis={null}
            rightAxis={{}}
          >
            <ChartsGrid horizontal />
            <CustomItemTooltip />
            <LinePlot />
            <MarkPlot />
            <ChartsXAxis axisId="my-x-axis" />
          </LineChartPro>
        </Box>
      </Stack>
    </>
  );
};

export default ScrolledUsers;
